import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, TrendingUp, Award, CheckCircle2, ArrowLeft, ShoppingBag, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";

export default function CourseDetail() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/courses/${slug}`)
      .then((r) => setCourse(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="container-x py-32 text-center">
        <h1 className="font-display text-4xl mb-4">Eğitim Bulunamadı</h1>
        <Link to="/egitimler" className="btn-outline">Katalog'a Dön</Link>
      </div>
    );
  }
  if (!course) {
    return <div className="container-x py-32 text-center text-white/50">Yükleniyor...</div>;
  }

  const price = new Intl.NumberFormat("tr-TR").format(course.price);

  return (
    <div>
      {/* Hero */}
      <section className="relative border-b border-white/10">
        <div className="absolute inset-0">
          <img src={course.image_url} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/85 to-black" />
        </div>
        <div className="container-x relative pt-16 pb-20">
          <Link to="/egitimler" data-testid="back-to-catalog" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-gold mb-8">
            <ArrowLeft className="w-4 h-4" /> Katalog
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8">
              <span className="text-[10px] uppercase tracking-[0.32em] text-gold bg-black/60 px-3 py-1 border border-gold-30">
                {course.category}
              </span>
              <h1 className="font-display text-4xl md:text-6xl mt-6 leading-tight" data-testid="course-title">
                {course.title}
              </h1>
              <p className="mt-6 text-lg text-white/70 max-w-2xl leading-relaxed">
                {course.short_description}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-white/70">
                <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-gold" />{course.duration}</span>
                <span className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-gold" />{course.level}</span>
                <span className="flex items-center gap-2"><Award className="w-4 h-4 text-gold" />Sertifikalı</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body split */}
      <section className="container-x py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left: content */}
          <div className="lg:col-span-8 space-y-16">
            <div>
              <span className="section-eyebrow">Program Hakkında</span>
              <h2 className="font-display text-3xl md:text-4xl mt-4 mb-6">Bu programda öğrenecekleriniz</h2>
              <p className="text-white/75 leading-relaxed whitespace-pre-line">
                {course.description}
              </p>
            </div>

            {course.outcomes?.length > 0 && (
              <div>
                <span className="section-eyebrow">Kazanımlar</span>
                <h2 className="font-display text-3xl md:text-4xl mt-4 mb-6">Program sonunda</h2>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {course.outcomes.map((o, i) => (
                    <li key={i} className="flex gap-3 items-start bg-surface border border-white/10 p-5">
                      <CheckCircle2 className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                      <span className="text-white/85 text-sm leading-relaxed">{o}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {course.curriculum?.length > 0 && (
              <div data-testid="curriculum">
                <span className="section-eyebrow">Müfredat</span>
                <h2 className="font-display text-3xl md:text-4xl mt-4 mb-8">Modüller</h2>
                <div className="space-y-4">
                  {course.curriculum.map((m, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.05 }}
                      className="bg-surface border border-white/10 p-6"
                    >
                      <div className="flex items-center gap-4 mb-4">
                        <div className="font-display text-2xl text-gold/60">{String(i + 1).padStart(2, "0")}</div>
                        <div className="font-display text-xl">{m.title}</div>
                      </div>
                      <div className="hairline mb-4" />
                      <ul className="space-y-2">
                        {(m.lessons || []).map((les, j) => (
                          <li key={j} className="flex items-center gap-3 text-sm text-white/70">
                            <span className="w-1.5 h-1.5 bg-gold rounded-full" />
                            {typeof les === "string" ? les : les?.title}
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Instructor mini */}
            <div>
              <span className="section-eyebrow">Eğitmen</span>
              <div className="mt-6 flex flex-col sm:flex-row gap-6 bg-surface border border-white/10 p-8">
                <img
                  src="/images/zulfikar-benli.webp"
                  alt="Zülfikar Benli"
                  className="w-28 h-28 object-cover border border-gold-30"
                />
                <div>
                  <div className="font-display text-2xl">Zülfikar Benli</div>
                  <div className="text-sm text-gold uppercase tracking-widest mt-1">Kurucu · Baş Eğitmen</div>
                  <p className="text-white/70 mt-3 text-sm leading-relaxed">
                    25+ yıl sektör deneyimi, 15+ yıl eğitmenlik. 500'den fazla öğrenci yetiştirdi.
                  </p>
                  <Link to="/egitmen" className="mt-4 inline-block text-sm text-gold hover:underline">
                    Profili incele →
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Right sticky sidebar */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="bg-surface border border-gold-30 p-8">
                <div className="text-[10px] uppercase tracking-[0.32em] text-white/50">Yatırım</div>
                <div className="font-display text-5xl text-gold mt-2" data-testid="course-price">₺{price}</div>
                <p className="text-xs text-white/50 mt-2">Tek seferlik ödeme · Ömür boyu erişim</p>

                <div className="hairline my-6" />

                <Link
                  to={`/kayit-formu?program=${course.slug}`}
                  data-testid="detail-buy-btn"
                  className="btn-primary w-full justify-center"
                >
                  <ShoppingBag className="w-4 h-4" /> Kayıt Ol
                </Link>
                <a
                  href={`https://wa.me/905543291119?text=${encodeURIComponent(`Merhaba, "${course.title}" için ücretsiz tanışma dersi almak istiyorum.`)}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="detail-preview-btn"
                  className="btn-outline w-full justify-center mt-3"
                >
                  <MessageCircle className="w-4 h-4" /> Ücretsiz Tanışma Dersi Talep Et
                </a>

                <p className="text-xs text-white/40 text-center mt-4">
                  Kayıt formunu doldurduğunuzda ödeme bilgileri (Havale/EFT) size gösterilir.
                </p>

                <div className="hairline my-6" />

                <ul className="space-y-3 text-sm text-white/80">
                  <li className="flex gap-3"><Clock className="w-4 h-4 text-gold mt-0.5" /> {course.duration} canlı ders (Google Meet)</li>
                  <li className="flex gap-3"><Award className="w-4 h-4 text-gold mt-0.5" /> Tamamlama sertifikası</li>
                  <li className="flex gap-3"><CheckCircle2 className="w-4 h-4 text-gold mt-0.5" /> Mobil ve masaüstü erişim</li>
                  <li className="flex gap-3"><CheckCircle2 className="w-4 h-4 text-gold mt-0.5" /> Ömür boyu güncellemeler</li>
                </ul>
              </div>

              <div className="text-xs text-white/50 text-center">
                Ödeme <span className="text-gold">Havale/EFT</span> veya Shopier ile güvenle gerçekleşir.
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
